import { useFlags } from 'launchdarkly-react-client-sdk';
import { isUndefined } from 'lodash';
import { ComponentProps, FC } from 'react';

import { PROMO_4 } from 'src/constants';
import { MICROCOPY } from 'src/data/microcopy/microcopyDictionary';
import { PromoPillBanner } from 'src/general/components/PromoPillBanner/PromoPillBanner';
import { processKilometers } from 'src/general/helpers/processProductApiData';
import { getMicrocopy } from 'src/stores/microcopyStore';
import { ProductDetailsPayload } from 'src/types/ProductDetail.type';

import { Car360V1 } from '../Car360V1/Car360V1';
import { WarrantyInfo } from '../WarrantyInfo/WarrantyInfo';
import styles from './CarOverview.module.scss';
import classNames from 'classnames';

const formatWarrantyInfo = (productDetails: ProductDetailsPayload): ComponentProps<typeof WarrantyInfo> => {
  const remainingWarranty: ComponentProps<typeof WarrantyInfo>['remainingWarranty'] = {
    warrantyExpiryKms: productDetails.warranty_expiry_kms,
    remainingMonths: productDetails.remaining_warranty_months,
    remainingKilometres: productDetails.remaining_warranty_kilometres,
    unlimitedKilometresFlag: productDetails.unlimited_kilometres_flag,
  };

  const showWarranty = showWarrantyInfo(remainingWarranty);

  const showEligible = showEligibleInfo(productDetails.kilometres);

  return {
    remainingWarranty,
    make: productDetails.make,
    showWarranty,
    showEligible,
  };
};

const WARRANTY_MONTHS_THRESHOLD = 3;
const WARRANTY_KILOMETRES_THRESHOLD = 5000;
const ELIGIBLE_KILOMETRES = 160000;

/**
 * Show warranty information if the following conditions are true:
 * - We have all of the warranty information
 * - The remaining months is over the allowed threshold
 * - The warranty is an 'unlimited kilometres' warranty, or the remaining
 *   kilometres is over the allowed threshold.
 */
const showWarrantyInfo = (
  remainingWarranty: ComponentProps<typeof WarrantyInfo>['remainingWarranty'],
): remainingWarranty is NonNullable<Required<ComponentProps<typeof WarrantyInfo>['remainingWarranty']>> =>
  !!remainingWarranty &&
  !Object.values(remainingWarranty).some(isUndefined) &&
  remainingWarranty.remainingMonths! >= WARRANTY_MONTHS_THRESHOLD &&
  (remainingWarranty.unlimitedKilometresFlag ||
    remainingWarranty.remainingKilometres! >= WARRANTY_KILOMETRES_THRESHOLD);

const showEligibleInfo = (kilometers: string) => {
  const carKilometers = processKilometers(kilometers);
  return carKilometers < ELIGIBLE_KILOMETRES;
};

export const CarOverview: FC<ProductDetailsPayload> = (productDetailsPayload) => {
  const flags = useFlags();
  const title = getMicrocopy(MICROCOPY.PDP_CAR_OVERVIEW_TITLE);
  const warrantyProps = formatWarrantyInfo(productDetailsPayload);
  const showBadges = flags?.uiBadgesEnabled && productDetailsPayload.badges && productDetailsPayload.badges.length > 0;

  if (!showBadges && !warrantyProps.showWarranty && !warrantyProps.showEligible) {
    return null;
  }

  return (
    <section className={styles.root}>
      {showBadges && (
        <>
          <h3 className={classNames(styles.header, 'c-fw-bold', 'c-fs-h5')}>{title}</h3>
          <Car360V1 items={productDetailsPayload.badges} />
        </>
      )}
      {(warrantyProps.showWarranty || warrantyProps.showEligible) && <WarrantyInfo {...warrantyProps} />}
      {productDetailsPayload.search_keywords.includes(PROMO_4) && <PromoPillBanner />}
    </section>
  );
};
